const SYNC_WRAPPER_CLASS = 'flip-slider-sync';
const UNSYNC_WRAPPER_CLASS = 'flip-slider-unsync';
const SYNC_FLIP_INTERVAL = 3000; // ms between each set
const MOBILE_BREAKPOINT_PX = 767; // match coating-brand mobile breakpoint
/** Pixels: if tops differ less than this, treat as same row (flex/grid row alignment). */
const VISUAL_ROW_TOLERANCE_PX = 48;

/**
 * Tie-break when two sliders are equally far from the current point.
 */
const compareFlipSlidersReadingOrder = (a, b) => {
  const ra = a.getBoundingClientRect();
  const rb = b.getBoundingClientRect();
  if (Math.abs(ra.top - rb.top) > VISUAL_ROW_TOLERANCE_PX) {
    return ra.top - rb.top;
  }
  return ra.left - rb.left;
};

/**
 * Order sliders by distance from the section's top-left (start). First = closest to
 * that point, second = next closest, and so on. Ties use reading order (top/left).
 */
const orderFlipSlidersByDistanceFromSectionStart = (sectionRoot, elements) => {
  const rootRect = sectionRoot.getBoundingClientRect();
  const originX = rootRect.left;
  const originY = rootRect.top;

  const centerOf = (el) => {
    const r = el.getBoundingClientRect();
    return {
      x: r.left + r.width / 2,
      y: r.top + r.height / 2,
    };
  };

  return Array.from(elements).sort((a, b) => {
    const ca = centerOf(a);
    const cb = centerOf(b);
    const da = Math.hypot(ca.x - originX, ca.y - originY);
    const db = Math.hypot(cb.x - originX, cb.y - originY);
    if (Math.abs(da - db) > 1e-3) {
      return da - db;
    }
    return compareFlipSlidersReadingOrder(a, b);
  });
};

export const flipSlider = () => {
  const containers = document.querySelectorAll('.flip-slider');
  const defaultDelay = 6000;

  const swiperInstances = [];

  containers.forEach((el, index) => {
    // Assign unique gallery names per slider to keep loops isolated
    const galleryId = `flip-gallery-${index}`;
    const galleryLinks = el.querySelectorAll('.flip-gallery-link');
    galleryLinks.forEach(link => {
      link.setAttribute('data-gallery', galleryId);
      link.setAttribute('data-type', 'image'); 
    });

    const delay = parseInt(el.dataset.autoplayDelay, 10) || defaultDelay;
    const offset = parseInt(el.dataset.autoplayOffset, 10) || 0;
    const rawDirection = el.dataset.flipDirection || el.dataset.cubeDirection || 'horizontal';
    const direction = String(rawDirection).toLowerCase();
    const isVertical = direction === 'vertical';
    const syncRoot = el.closest(`.${SYNC_WRAPPER_CLASS}`);
    const unsyncRoot = syncRoot ? null : el.closest(`.${UNSYNC_WRAPPER_CLASS}`);

    if (isVertical) {
      el.classList.add('flip-slider--vertical');
    }

    const useAutoplay = !syncRoot && !unsyncRoot;
    const autoplayConfig = {
      delay,
      disableOnInteraction: false,
    };

    // Horizontal: flip effect. Vertical: creative effect with rotateX for a true vertical flip (not cube).
    const swiper = new Swiper(el, {
      effect: isVertical ? 'creative' : 'flip',
      direction: isVertical ? 'vertical' : 'horizontal',
      ...(isVertical
        ? {
          creativeEffect: {
            limitProgress: 1,
            perspective: true,
            prev: {
              translate: [0, 0, 0],
              rotate: [-180, 0, 0],
              origin: 'center center',
              shadow: true,
            },
            next: {
              translate: [0, 0, 0],
              rotate: [180, 0, 0],
              origin: 'center center',
              shadow: true,
            },
          },
        }
        : {
          flipEffect: {
            slideShadows: true,
            limitRotation: true,
          },
        }),
      speed: 800,
      grabCursor: true,
      loop: true,
      allowTouchMove: true,
      autoplay: useAutoplay ? autoplayConfig : false,
    });

    // Stagger: delay autoplay start so not all sliders flip at once
    if (useAutoplay && offset > 0 && swiper.autoplay) {
      swiper.autoplay.stop();
      window.setTimeout(() => swiper.autoplay?.start(), offset);
    }

    const flipSetBase = syncRoot ? (el.dataset.flipSet || '').trim() : null;
    const flipSetMobileRaw = (el.dataset.flipSetMobile || '').trim();
    const flipSetMobile = flipSetMobileRaw && flipSetMobileRaw !== 'undefined' ? flipSetMobileRaw : '';
    const isMobileViewport = () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
    const flipSet = syncRoot && flipSetBase
      ? (flipSetMobile && isMobileViewport() ? flipSetMobile : flipSetBase)
      : null;

    swiperInstances.push({
      el,
      swiper,
      syncRoot,
      unsyncRoot,
      flipSet: flipSet || null,
    });
  });

  // Sync sections: each .flip-slider-sync runs its own set-based flip (by data-flip-set)
  const syncWrappers = document.querySelectorAll(`.${SYNC_WRAPPER_CLASS}`);
  syncWrappers.forEach((root) => {
    const instancesInRoot = swiperInstances.filter((i) => i.syncRoot === root);
    const setOrder = [];
    const setMap = new Map();

    instancesInRoot.forEach(({ swiper, flipSet }) => {
      if (!flipSet) return;
      if (!setMap.has(flipSet)) {
        setOrder.push(flipSet);
        setMap.set(flipSet, []);
      }
      setMap.get(flipSet).push(swiper);
    });

    if (setOrder.length === 0) return;

    const flipSetAtIndex = (index) => {
      const setId = setOrder[index];
      if (setId) setMap.get(setId).forEach((s) => s.slideNext());
    };

    setOrder.forEach((_, i) => {
      window.setTimeout(() => flipSetAtIndex(i), i * SYNC_FLIP_INTERVAL);
    });

    const fullCycle = setOrder.length * SYNC_FLIP_INTERVAL;
    const interval = window.setInterval(() => {
      setOrder.forEach((_, i) => {
        window.setTimeout(() => flipSetAtIndex(i), i * SYNC_FLIP_INTERVAL);
      });
    }, fullCycle);

    const cleanup = () => window.clearInterval(interval);
    window.addEventListener('beforeunload', cleanup);
  });

  // Unsync sections: one flip at a time — closest to section top-left first, then
  // next closest to that same point, etc., then repeat (cycle)
  const unsyncWrappers = document.querySelectorAll(`.${UNSYNC_WRAPPER_CLASS}`);
  const startUnsyncCycles = () => {
    unsyncWrappers.forEach((root) => {
      const slidersRaw = root.querySelectorAll('.flip-slider');
      const slidersOrdered = orderFlipSlidersByDistanceFromSectionStart(root, slidersRaw);
      const instancesInRoot = slidersOrdered
        .map((sliderEl) => swiperInstances.find((i) => i.el === sliderEl))
        .filter(Boolean);

      if (instancesInRoot.length === 0) return;

      const firstEl = instancesInRoot[0].el;
      const stepDelay =
        parseInt(root.dataset.autoplayDelay, 10) ||
        parseInt(firstEl.dataset.autoplayDelay, 10) ||
        defaultDelay;

      let index = 0;
      const advance = () => {
        instancesInRoot[index].swiper.slideNext();
        index = (index + 1) % instancesInRoot.length;
      };

      const intervalId = window.setInterval(advance, stepDelay);
      const cleanup = () => window.clearInterval(intervalId);
      window.addEventListener('beforeunload', cleanup);
    });
  };
  requestAnimationFrame(startUnsyncCycles);

  // Init GLightbox
  if (typeof GLightbox !== 'undefined') {
    const lightbox = GLightbox({
      selector: '.flip-gallery-link',
      loop: true
    });
    
    lightbox.on('open', () => {
      document.querySelector('.gcontainer')?.classList.add('flip-gallery-skin');
      document.querySelector('.goverlay')?.classList.add('flip-gallery-skin');
    });
    
    lightbox.on('close', () => {
      document.querySelector('.gcontainer')?.classList.remove('flip-gallery-skin');
      document.querySelector('.goverlay')?.classList.remove('flip-gallery-skin');
    });
  }
};
